import { client } from "./supabase";

/**
 * Detecta pares de venta de divisas (egreso USD + ingreso CLP del mismo día)
 * y los registra automáticamente en movimientos_caja como dos entradas
 * vinculadas por movimiento_padre_id.
 *
 * Criterios de match:
 * - Un egreso USD en cuenta USD (tipo='egreso', moneda='USD')
 * - Un ingreso CLP en cuenta CLP (tipo='ingreso', moneda='CLP')
 * - Misma fecha
 * - descripcion_raw ILIKE '%venta divisas%' o '%ingreso por venta de divisas%'
 * - Ambos en estado='pendiente'
 */
export const procesarVentaDivisas = async ({ movimientos, cuentas, pais }) => {
  // eslint-disable-next-line no-console
  console.log("[procesarVentaDivisas] iniciando, movimientos:", movimientos?.length, "cuentas:", cuentas?.length);
  const cuentaUSD = cuentas.find((c) => c.moneda === "USD");
  const cuentaCLP = cuentas.find((c) => c.moneda === "CLP");
  // eslint-disable-next-line no-console
  console.log("[procesarVentaDivisas] cuentaUSD:", cuentaUSD?.id, "cuentaCLP:", cuentaCLP?.id);
  if (!cuentaUSD || !cuentaCLP) return;

  const egresoUSD = movimientos.filter(
    (m) =>
      m.cuenta_bancaria_id === cuentaUSD.id &&
      m.tipo === "egreso" &&
      m.moneda === "USD" &&
      m.estado === "pendiente" &&
      (m.descripcion_raw || "").toLowerCase().includes("divisas")
  );

  const ingresoCLP = movimientos.filter(
    (m) =>
      m.cuenta_bancaria_id === cuentaCLP.id &&
      m.tipo === "ingreso" &&
      m.moneda === "CLP" &&
      m.estado === "pendiente" &&
      (m.descripcion_raw || "").toLowerCase().includes("divisas")
  );

  // eslint-disable-next-line no-console
  console.log(
    "[procesarVentaDivisas] egresoUSD encontrados:",
    egresoUSD?.length,
    "ingresoCLP encontrados:",
    ingresoCLP?.length
  );

  for (const usd of egresoUSD) {
    const clp = ingresoCLP.find((c) => c.fecha === usd.fecha && !c._usado);
    if (!clp) continue;
    clp._usado = true;

    try {
      // Fila madre — pata USD (egreso). Upsert idempotente por movimiento_bancario_id.
      const { data: madreData, error: madreError } = await client
        .from("movimientos_caja")
        .upsert(
          {
            pais,
            tipo: "pago",
            fecha: usd.fecha,
            monto: Math.abs(usd.monto),
            moneda: "USD",
            estado: "confirmado",
            descripcion: `Venta Divisas ${usd.fecha}`,
            entidad_tipo: "otro",
            entidad_id: null,
            entidad_nombre: "Cambio de Moneda",
            categoria_pago: "costo_financiero",
            cuenta_er: null,
            origen: "clay",
            movimiento_bancario_id: usd.id,
            registrado_por: 1,
          },
          { onConflict: "movimiento_bancario_id", ignoreDuplicates: false }
        )
        .select()
        .single();
      if (madreError) throw madreError;
      const madre = madreData;
      if (!madre?.id) continue;

      // Fila hija — pata CLP (ingreso). Upsert idempotente por movimiento_bancario_id.
      const { data: hijaData, error: hijaError } = await client
        .from("movimientos_caja")
        .upsert(
          {
            pais,
            tipo: "cobro",
            fecha: clp.fecha,
            monto: Math.abs(clp.monto),
            moneda: "CLP",
            estado: "confirmado",
            descripcion: `Venta Divisas ${clp.fecha}`,
            entidad_tipo: "otro",
            entidad_id: null,
            entidad_nombre: "Cambio de Moneda",
            categoria_pago: "costo_financiero",
            cuenta_er: null,
            origen: "clay",
            movimiento_bancario_id: clp.id,
            movimiento_padre_id: madre.id,
            registrado_por: 1,
          },
          { onConflict: "movimiento_bancario_id", ignoreDuplicates: false }
        )
        .select()
        .single();
      if (hijaError) throw hijaError;
      const hija = hijaData;
      if (!hija?.id) continue;

      // Marcar ambos bancarios como procesados
      await client
        .from("movimientos_bancarios")
        .update({
          estado: "procesado",
          movimiento_caja_id: madre.id,
          procesado_por: 1,
          procesado_at: new Date().toISOString(),
        })
        .eq("id", usd.id);

      await client
        .from("movimientos_bancarios")
        .update({
          estado: "procesado",
          movimiento_caja_id: hija.id,
          procesado_por: 1,
          procesado_at: new Date().toISOString(),
        })
        .eq("id", clp.id);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Error procesando venta divisas par", usd.id, "-", clp?.id, ":", err?.message || err);
    }
  }

  // Caso inverso: Compra de Divisas (egreso CLP → ingreso USD)
  const egresosCLP = movimientos.filter(
    (m) =>
      m.cuenta_bancaria_id === cuentaCLP.id &&
      m.tipo === "egreso" &&
      m.moneda === "CLP" &&
      m.estado === "pendiente" &&
      (m.descripcion_raw || "").toLowerCase().includes("divisas")
  );

  const ingresosUSD = movimientos.filter(
    (m) =>
      m.cuenta_bancaria_id === cuentaUSD.id &&
      m.tipo === "ingreso" &&
      m.moneda === "USD" &&
      m.estado === "pendiente" &&
      (m.descripcion_raw || "").toLowerCase().includes("divisas")
  );

  // eslint-disable-next-line no-console
  console.log(
    "[procesarVentaDivisas] egresosCLP encontrados:",
    egresosCLP?.length,
    "ingresosUSD encontrados:",
    ingresosUSD?.length
  );

  for (const clp of egresosCLP) {
    const usdIngreso = ingresosUSD.find((u) => u.fecha === clp.fecha && !u._usado);
    if (!usdIngreso) continue;
    usdIngreso._usado = true;

    try {
      // Fila madre — pata CLP (egreso). Upsert idempotente por movimiento_bancario_id.
      const { data: madreData, error: madreError } = await client
        .from("movimientos_caja")
        .upsert(
          {
            pais,
            tipo: "pago",
            fecha: clp.fecha,
            monto: Math.abs(clp.monto),
            moneda: "CLP",
            estado: "confirmado",
            descripcion: `Compra Divisas ${clp.fecha}`,
            entidad_tipo: "otro",
            entidad_id: null,
            entidad_nombre: "Cambio de Moneda",
            categoria_pago: "costo_financiero",
            cuenta_er: null,
            origen: "clay",
            movimiento_bancario_id: clp.id,
            registrado_por: 1,
          },
          { onConflict: "movimiento_bancario_id", ignoreDuplicates: false }
        )
        .select()
        .single();
      if (madreError) throw madreError;
      if (!madreData?.id) continue;

      // Fila hija — pata USD (ingreso). Upsert idempotente por movimiento_bancario_id.
      const { data: hijaData, error: hijaError } = await client
        .from("movimientos_caja")
        .upsert(
          {
            pais,
            tipo: "cobro",
            fecha: usdIngreso.fecha,
            monto: Math.abs(usdIngreso.monto),
            moneda: "USD",
            estado: "confirmado",
            descripcion: `Compra Divisas ${usdIngreso.fecha}`,
            entidad_tipo: "otro",
            entidad_id: null,
            entidad_nombre: "Cambio de Moneda",
            categoria_pago: "costo_financiero",
            cuenta_er: null,
            origen: "clay",
            movimiento_bancario_id: usdIngreso.id,
            movimiento_padre_id: madreData.id,
            registrado_por: 1,
          },
          { onConflict: "movimiento_bancario_id", ignoreDuplicates: false }
        )
        .select()
        .single();
      if (hijaError) throw hijaError;
      if (!hijaData?.id) continue;

      // Marcar ambos bancarios como procesados
      await client
        .from("movimientos_bancarios")
        .update({
          estado: "procesado",
          movimiento_caja_id: madreData.id,
          procesado_por: 1,
          procesado_at: new Date().toISOString(),
        })
        .eq("id", clp.id);

      await client
        .from("movimientos_bancarios")
        .update({
          estado: "procesado",
          movimiento_caja_id: hijaData.id,
          procesado_por: 1,
          procesado_at: new Date().toISOString(),
        })
        .eq("id", usdIngreso.id);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Error procesando compra divisas par", clp.id, "-", usdIngreso?.id, ":", err?.message || err);
    }
  }
};
